import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
// Clean build reloaded
import './App.css';
import DashboardPage from './pages/DashboardPage';
import { fetchWithCache } from './utils/apiCache';
import { IntelligenceProvider, useIntelligence } from './context/IntelligenceContext';
import { TrustLayerPopover } from './components/TrustBadge';
import { OfflineBanner } from './components/StateDisplays';
import WhyModal from './components/WhyModal';
import IntelligenceModal from './components/IntelligenceModal';
import VisitorCounter from './components/VisitorCounter';
import LocationSelector from './components/LocationSelector';
import HoneypotDecoy from './components/HoneypotDecoy';
import { 
  IconOverview, 
  IconAnalytics,
  IconSearch, 
  IconTrends, 
  IconOutlook, 
  IconProfile, 
  // IconBrandLogo,
  IconGlobe,
  IconBell,
  IconCheck,
  IconClose,
  IconZap,
  IconTrending,
  IconTarget
  // IconArrowRight
} from './components/Icons';
import lysarqLogoImg from './assets/lysarq-logo.png';
import { API_URL } from './config/api';
import { 
  AboutPage, 
  MethodologyPage, 
  DataSourcesPage, 
  DataDictionaryPage, 
  FAQPage 
} from './pages/PublicInfoPages';
import SiteFooter from './components/SiteFooter';
import translations, { getTranslation } from './utils/translations';

// Pre-warmed route-level code splitting for sub-10ms instantaneous tab switching
const analyticsPagePromise = () => import('./pages/AnalyticsPage');
const searchPagePromise = () => import('./pages/SearchPage');
const trendsPagePromise = () => import('./pages/TrendsPage');
const forecastPagePromise = () => import('./pages/ForecastPage');
const profilePagePromise = () => import('./pages/ProfilePage');
const commandPalettePromise = () => import('./components/CommandPalette');

const AnalyticsPage = lazy(analyticsPagePromise);
const SearchPage = lazy(searchPagePromise);
const TrendsPage = lazy(trendsPagePromise);
const ForecastPage = lazy(forecastPagePromise);
const ProfilePage = lazy(profilePagePromise);
const CommandPalette = lazy(commandPalettePromise);

const prewarmAllPages = () => {
  analyticsPagePromise();
  searchPagePromise();
  trendsPagePromise();
  forecastPagePromise();
  profilePagePromise();
  commandPalettePromise();
};

// Centralized translations are imported from ./utils/translations

const NAV_ITEMS = [
  { id: 'home', Icon: IconOverview, key: 'home' },
  { id: 'analytics', Icon: IconAnalytics, key: 'analytics' },
  { id: 'search', Icon: IconSearch, key: 'search' },
  { id: 'trends', Icon: IconTrends, key: 'trends' },
  { id: 'forecast', Icon: IconOutlook, key: 'forecast' },
  { id: 'profile', Icon: IconProfile, key: 'profile' },
];

function WorkspaceFallback() {
  return (
    <div style={{ padding: '2rem 1rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ height: '140px', borderRadius: '16px', background: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(8px)', border: '1px solid #e8edf5' }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
        <div style={{ height: '130px', borderRadius: '14px', background: 'rgba(255, 255, 255, 0.7)', border: '1px solid #e8edf5' }} />
        <div style={{ height: '130px', borderRadius: '14px', background: 'rgba(255, 255, 255, 0.7)', border: '1px solid #e8edf5' }} />
        <div style={{ height: '130px', borderRadius: '14px', background: 'rgba(255, 255, 255, 0.7)', border: '1px solid #e8edf5' }} />
        <div style={{ height: '130px', borderRadius: '14px', background: 'rgba(255, 255, 255, 0.7)', border: '1px solid #e8edf5' }} />
      </div>
    </div>
  );
}

function AppContent() {
  const { 
    district, 
    setDistrict, 
    userPincode, 
    setUserPincode, 
    selectedSector,
    setSelectedSector,
    isOffline, 
    whyModalData, 
    closeWhyModal,
    alertsFeed,
    unreadAlertCount,
    markAlertAsRead,
    markAllAlertsAsRead,
    dismissAlert,
    activeToast,
    setActiveToast,
    activeModal,
    openModal,
    closeModal,
    saveCurrentView
  } = useIntelligence();

  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const path = window.location.pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
      if (['about', 'methodology', 'data-sources', 'data-dictionary', 'faq'].includes(path)) {
        return path;
      }
      const params = new URLSearchParams(window.location.search);
      const p = params.get('page');
      return ['home', 'analytics', 'search', 'trends', 'forecast', 'profile', 'about', 'methodology', 'data-sources', 'data-dictionary', 'faq'].includes(p) ? p : 'home';
    } catch {
      return 'home';
    }
  });

  const [language, setLanguage] = useState(() => {
    try {
      const stored = localStorage.getItem('preferred_language');
      return ['en', 'ta', 'ml', 'hi'].includes(stored) ? stored : 'en';
    } catch {
      return 'en';
    }
  });

  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    try {
      localStorage.setItem('preferred_language', newLang);
    } catch {}
  };
  const [regions, setRegions] = useState({ all: null, states: [] });
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [visitedPages, setVisitedPages] = useState(() => new Set([currentPage]));
  const [isUpdatingContext, setIsUpdatingContext] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(() => Date.now());
  const [freshnessText, setFreshnessText] = useState('Live Synced · Just now');
  
  const indicatorRef = useRef(null);
  const dockContainerRef = useRef(null);
  const tabButtonRefs = useRef({});

  // Click outside to close notification dropdown
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.header-notification-wrapper')) {
        setIsNotificationOpen(false);
      }
    };
    if (isNotificationOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isNotificationOpen]);

  // Primary Lysarq Navbar State
  const [isBannerDismissed, setIsBannerDismissed] = useState(() => {
    try {
      return sessionStorage.getItem('ll-banner-closed') === '1';
    } catch {
      return false;
    }
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileToolsOpen, setIsMobileToolsOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const toolsDropdownRef = useRef(null);

  const closeAnnouncementBanner = () => {
    setIsBannerDismissed(true);
    try {
      sessionStorage.setItem('ll-banner-closed', '1');
    } catch (e) {}
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileToolsOpen(false);
  };

  // Close tools dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(e.target)) {
        setIsToolsDropdownOpen(false);
      }
    };
    if (isToolsDropdownOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isToolsDropdownOpen]);

  // Handle ESC key for mobile menu
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeMobileMenu();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Prevent background scroll when mobile menu is active
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const t = getTranslation(language, 'nav');

  // Real-time relative freshness timer
  useEffect(() => {
    const updateFreshness = () => {
      const seconds = Math.floor((Date.now() - lastSyncedAt) / 1000);
      if (seconds < 45) {
        setFreshnessText('Live Synced · Just now');
      } else if (seconds < 90) {
        setFreshnessText('Updated 1 min ago · Live Synced');
      } else if (seconds < 3600) {
        const mins = Math.floor(seconds / 60);
        setFreshnessText(`Updated ${mins} min ago · Live Synced`);
      } else {
        const hrs = Math.floor(seconds / 3600);
        setFreshnessText(`Updated ${hrs}h ago · Live Synced`);
      }
    };

    updateFreshness();
    const interval = setInterval(updateFreshness, 10000);
    return () => clearInterval(interval);
  }, [lastSyncedAt]);

  // Dynamic Document Title for AEO / GEO Entity Discoverability
  useEffect(() => {
    const titles = {
      home: 'LYSARQ MSME — Business Intelligence & Market Telemetry',
      analytics: 'Analytics & Sector Trajectories — Lysarq MSME Intelligence',
      search: 'Explore & Pin Code Directory — Lysarq MSME Intelligence',
      trends: 'Market Trends & Momentum Studio — Lysarq MSME Intelligence',
      forecast: 'Growth Outlook & Predictive Intelligence — Lysarq MSME Intelligence',
      profile: 'Enterprise Profile & Matchmaking — Lysarq MSME Intelligence',
      about: 'About Lysarq MSME Intelligence — Enterprise Analytics & Entity Model',
      methodology: 'Methodology & Data Processing Pipeline — Lysarq MSME Intelligence',
      'data-sources': 'Authoritative Data Sources & Provenance — Lysarq MSME Intelligence',
      'data-dictionary': 'Data Dictionary & Metric Standards — Lysarq MSME Intelligence',
      faq: 'Frequently Asked Questions (FAQ) — Lysarq MSME Intelligence',
    };
    if (titles[currentPage]) {
      document.title = titles[currentPage];
    }
  }, [currentPage]);

  // Reset sync timer on scope or district changes
  useEffect(() => {
    setLastSyncedAt(Date.now());
  }, [district, selectedSector]);

  // Sync initial URL params to context if present
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const d = params.get('district');
      const s = params.get('sector');
      const pin = params.get('pincode');
      const p = params.get('page');
      if (d) setDistrict(d);
      if (s) setSelectedSector(s);
      if (pin && p && p !== 'home') setUserPincode(pin);
    } catch (e) {
      console.warn('URL sync init warning:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update URL search query without reloading page
  useEffect(() => {
    try {
      const params = new URLSearchParams();
      if (currentPage !== 'home') params.set('page', currentPage);
      if (district && district !== 'all') params.set('district', district);
      if (selectedSector) params.set('sector', selectedSector);
      if (userPincode && currentPage !== 'home') params.set('pincode', userPincode);
      
      const newQuery = params.toString();
      const newUrl = newQuery ? `${window.location.pathname}?${newQuery}` : window.location.pathname;
      window.history.replaceState({ page: currentPage, district, sector: selectedSector, pincode: userPincode }, '', newUrl);
    } catch (e) {
      console.warn('URL update warning:', e);
    }
  }, [currentPage, district, selectedSector, userPincode]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state?.page) {
        setCurrentPage(e.state.page);
        if (e.state.sector !== undefined) setSelectedSector(e.state.sector);
        if (e.state.pincode !== undefined) setUserPincode(e.state.pincode);
        if (e.state.district !== undefined) setDistrict(e.state.district);
      } else {
        const params = new URLSearchParams(window.location.search);
        const p = params.get('page');
        const s = params.get('sector');
        const pin = params.get('pincode');
        const d = params.get('district');
        if (p) setCurrentPage(p);
        if (s !== null) setSelectedSector(s);
        if (pin !== null && p && p !== 'home') setUserPincode(pin);
        if (d !== null) setDistrict(d);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [setDistrict, setSelectedSector, setUserPincode]);

  // Keyboard shortcut listener: Ctrl+K / Cmd+K (Command Palette) and Ctrl+S / Cmd+S (Save Preset)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen((prev) => !prev);
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        // Intercept ONLY when not inside an input, textarea, or contenteditable element
        const activeTag = document.activeElement ? document.activeElement.tagName.toLowerCase() : '';
        const isEditable = document.activeElement && (
          activeTag === 'input' || 
          activeTag === 'textarea' || 
          document.activeElement.isContentEditable
        );
        if (!isEditable) {
          e.preventDefault();
          openModal('preset');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openModal]);

  // Feedback indicator when district or sector changes
  const prevContextRef = useRef({ district, selectedSector });
  useEffect(() => {
    if (prevContextRef.current.district !== district || prevContextRef.current.selectedSector !== selectedSector) {
      prevContextRef.current = { district, selectedSector };
      setIsUpdatingContext(true);
      const timer = setTimeout(() => setIsUpdatingContext(false), 700);
      return () => clearTimeout(timer);
    }
  }, [district, selectedSector]);

  // Scroll-aware navigation dock and floating CTA pill handling (DOM ref-based: 0 React re-renders on scroll for sub-50ms INP)
  const navHubRef = useRef(null);
  const ctaRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const scrollIdleTimerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollYRef.current;
      const el = ctaRef.current;

      // Minimize navigation dock when scrolled > 40px
      if (navHubRef.current) {
        if (currentY > 40) {
          navHubRef.current.classList.add('is-scrolled');
        } else {
          navHubRef.current.classList.remove('is-scrolled');
        }
      }

      if (el) {
        // Scrolling down fast → hide
        if (delta > 8 && currentY > 120) {
          el.classList.add('scrolled-away');
        }
        // Scrolling up → show
        if (delta < -5) {
          el.classList.remove('scrolled-away');
        }
      }

      lastScrollYRef.current = currentY;

      // Re-show CTA after 200ms idle
      clearTimeout(scrollIdleTimerRef.current);
      scrollIdleTimerRef.current = setTimeout(() => {
        if (ctaRef.current) {
          ctaRef.current.classList.remove('scrolled-away');
        }
      }, 200);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check on mount
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollIdleTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setVisitedPages((prev) => (prev.has(currentPage) ? prev : new Set(prev).add(currentPage)));
  }, [currentPage]);

  // Instantaneous hardware-accelerated indicator transform (0 React re-renders)
  const applyIndicatorPosition = useCallback((targetPage, immediate = false) => {
    const activeBtn = tabButtonRefs.current[targetPage];
    const container = dockContainerRef.current;
    const indicator = indicatorRef.current;
    if (activeBtn && container && indicator) {
      const containerRect = container.getBoundingClientRect();
      const btnRect = activeBtn.getBoundingClientRect();
      const left = Math.round(btnRect.left - containerRect.left);
      const width = Math.round(btnRect.width);
      if (width > 0) {
        if (immediate) {
          indicator.style.transition = 'none';
        }
        indicator.style.transform = `translate3d(${left}px, 0, 0)`;
        indicator.style.width = `${width}px`;
        indicator.style.opacity = '1';
        if (immediate) {
          requestAnimationFrame(() => {
            if (indicatorRef.current) {
              indicatorRef.current.style.transition = '';
            }
          });
        }
      }
    }
  }, []);

  // Prewarm chunks and calculate indicator position on mount, tab changes, and language shifts
  useEffect(() => {
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(prewarmAllPages);
    } else {
      setTimeout(prewarmAllPages, 150);
    }

    // Immediate calculation + deferred layout stabilization passes on language change
    applyIndicatorPosition(currentPage, true);
    const rafId = requestAnimationFrame(() => applyIndicatorPosition(currentPage, true));
    const t1 = setTimeout(() => applyIndicatorPosition(currentPage, true), 50);
    const t2 = setTimeout(() => applyIndicatorPosition(currentPage, true), 150);

    const handleResize = () => {
      applyIndicatorPosition(currentPage, true);
    };
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleResize, { passive: true });

    let ro;
    const container = dockContainerRef.current;
    if (typeof ResizeObserver !== 'undefined' && container) {
      ro = new ResizeObserver(() => {
        applyIndicatorPosition(currentPage, true);
      });
      ro.observe(container);
      Object.values(tabButtonRefs.current).forEach((btn) => {
        if (btn) ro.observe(btn);
      });
    }

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [applyIndicatorPosition, currentPage, language]);

  // Fetch districts list with in-memory caching
  useEffect(() => {
    let isMounted = true;
    fetchWithCache(`${API_URL}/districts`)
      .then((data) => {
        if (!isMounted) return;
        setRegions({
          all: data.all || null,
          states: data.states || [],
        });
      })
      .catch((err) => console.error('Districts fetch error:', err));
    return () => { isMounted = false; };
  }, []);

  // Compute selected district display name
  const currentDistrictName = useMemo(() => {
    if (district === 'all') return 'India (All States)';
    const matchingState = (regions.states || []).find((s) => s.code.toLowerCase() === district.toLowerCase());
    if (matchingState) return matchingState.name;
    for (const s of regions.states || []) {
      const match = (s.districts || []).find((d) => d.code.toLowerCase() === district.toLowerCase());
      if (match) return `${match.name}, ${s.name.replace(' (All Districts)', '')}`;
    }
    return district.charAt(0).toUpperCase() + district.slice(1);
  }, [district, regions]);

  const handleNavigate = useCallback((page, params = {}) => {
    if (params.sector !== undefined) setSelectedSector(params.sector);
    if (params.pincode !== undefined) setUserPincode(params.pincode);
    if (params.district !== undefined) setDistrict(params.district);

    // 1. Instant spring slide with 0ms animation start latency
    applyIndicatorPosition(page);

    // 2. Immediate page switch
    setCurrentPage(page);

    // 3. Instant scroll reset
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [applyIndicatorPosition, setDistrict, setSelectedSector, setUserPincode]);

  const renderNotificationCenter = () => (
    <div className="header-notification-wrapper">
      <button
        type="button"
        className={`header-bell-btn ${isNotificationOpen ? 'active' : ''}`}
        onClick={() => setIsNotificationOpen((prev) => !prev)}
        title="Intelligence Alerts & Live Triggers"
        aria-label="Intelligence Alerts"
      >
        <IconBell width={16} height={16} />
        {unreadAlertCount > 0 && (
          <span className="bell-unread-badge">
            {unreadAlertCount}
          </span>
        )}
      </button>

      {/* Notification Center Popover */}
      {isNotificationOpen && (
        <div className="notification-popover-card">
          <div className="notification-popover-header">
            <div>
              <span className="notif-popover-title">Intelligence Alerts</span>
              <span className="notif-popover-sub">{unreadAlertCount} unread triggers</span>
            </div>
            {unreadAlertCount > 0 && (
              <button
                type="button"
                className="notif-mark-read-btn"
                onClick={markAllAlertsAsRead}
              >
                <IconCheck width={12} height={12} />
                <span>Mark all read</span>
              </button>
            )}
          </div>

          <div className="notification-items-list">
            {alertsFeed && alertsFeed.length > 0 ? (
              alertsFeed.map((alert) => (
                <div
                  key={alert.id}
                  className={`notification-item-row ${alert.read ? 'read' : 'unread'} ${alert.urgency || 'moderate'}`}
                  onClick={() => {
                    markAlertAsRead(alert.id);
                    if (alert.targetTab) {
                      handleNavigate(alert.targetTab, alert.targetParams || {});
                      setIsNotificationOpen(false);
                    }
                  }}
                >
                  <div className="notif-item-icon">
                    {alert.urgency === 'high' ? (
                      <IconTrending width={14} height={14} />
                    ) : alert.urgency === 'opportunity' ? (
                      <IconZap width={14} height={14} />
                    ) : (
                      <IconTarget width={14} height={14} />
                    )}
                  </div>
                  <div className="notif-item-body">
                    <div className="notif-item-title-row">
                      <strong className="notif-item-title">{alert.title}</strong>
                      <span className="notif-item-time">{alert.timestamp}</span>
                    </div>
                    <p className="notif-item-desc">{alert.description}</p>
                    <span className="notif-explore-link">Explore in {alert.targetTab?.toUpperCase() || 'Analytics'} &rarr;</span>
                  </div>
                  <button
                    type="button"
                    className="notif-dismiss-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissAlert(alert.id);
                    }}
                    title="Dismiss alert"
                  >
                    <IconClose width={10} height={10} />
                  </button>
                </div>
              ))
            ) : (
              <div className="empty-notifs-view">
                <p>No active alerts right now. You can adjust your business alerts in My Business Profile.</p>
              </div>
            )}
          </div>

          <div className="notification-popover-footer">
            <button
              type="button"
              className="notif-footer-link"
              onClick={() => {
                handleNavigate('profile');
                setIsNotificationOpen(false);
              }}
            >
              <span>Set Your Alert Preferences in My Business &rarr;</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="app">
      {/* Offline Status Alert */}
      {isOffline && <OfflineBanner />}

      {/* Decision Factor Explanation Modal */}
      {whyModalData && <WhyModal data={whyModalData} onClose={closeWhyModal} />}

      {/* Universal In-Web Modal: Preset Save / Export / Share */}
      <IntelligenceModal
        isOpen={Boolean(activeModal)}
        type={activeModal || 'preset'}
        onClose={closeModal}
        district={district}
        selectedSector={selectedSector}
        userPincode={userPincode}
        onSavePreset={(title) => {
          saveCurrentView(title);
          setActiveToast({
            title: 'View Saved',
            description: `Saved "${title}" to your saved views.`,
            urgency: 'opportunity'
          });
        }}
      />

      {/* Executive Command Palette (Ctrl+K) */}
      <Suspense fallback={null}>
        {isCommandPaletteOpen && (
          <CommandPalette
            isOpen={isCommandPaletteOpen}
            onClose={() => setIsCommandPaletteOpen(false)}
            onNavigate={handleNavigate}
          />
        )}
      </Suspense>

      {/* Floating Real-time Dynamic Toast Alert */}
      {activeToast && (
        <div className="floating-alert-toast" role="alert">
          <div className="toast-icon-pill">
            <IconZap width={15} height={15} />
          </div>
          <div className="toast-content">
            <strong className="toast-title">{activeToast.title}</strong>
            <p className="toast-desc">{activeToast.description}</p>
          </div>
          <div className="toast-actions">
            {activeToast.targetTab && (
              <button
                type="button"
                className="toast-action-btn"
                onClick={() => {
                  handleNavigate(activeToast.targetTab, activeToast.targetParams || {});
                  setActiveToast(null);
                }}
              >
                View &rarr;
              </button>
            )}
            <button
              type="button"
              className="toast-close-btn"
              onClick={() => setActiveToast(null)}
              aria-label="Close notification"
            >
              <IconClose width={12} height={12} />
            </button>
          </div>
        </div>
      )}

      {/* ✦ PLATFORMS TICKER BANNER (BLUE & ORANGE) ✦ */}
      {!isBannerDismissed && (
        <div id="ll-announce-bar">
          <div className="ll-announce-inner">
            <div className="ll-marquee-wrap">
              <div className="ll-marquee-track">
                <a href="https://lysarqsolutions.com/platforms/lysarq-learn" className="ll-m-link">
                  🎓 <strong className="ll-plat-name">LysarQLearN</strong> <span className="ll-plat-sub">(LMS &amp; Auto-Grading)</span>
                </a>
                <span className="ll-m-sep">•</span>
                <a 
                  href="https://msme-intelligence.lysarqsolutions.com" 
                  className="ll-m-link" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (window.location.hostname.includes('msme-intelligence') || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                      e.preventDefault();
                      handleNavigate('home');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  📊 <strong className="ll-plat-name">MSME BI</strong> <span className="ll-plat-sub">(Business Intelligence)</span>
                </a>
                <span className="ll-m-sep">•</span>
                <a href="https://lysarqsolutions.com/platforms/digital-factory" className="ll-m-link">
                  🏭 <strong className="ll-plat-name">Digital Factory</strong> <span className="ll-plat-sub">(Workflow Automation)</span>
                </a>
                <span className="ll-m-sep">•</span>
                <a href="https://lysarqsolutions.com/platforms/circuit-builder" className="ll-m-link">
                  ⚡ <strong className="ll-plat-name">Circuit Builder</strong> <span className="ll-plat-sub">(Virtual Labs)</span>
                </a>
                <span className="ll-m-sep">•</span>
                <a href="https://lysarqsolutions.com/platforms/whatsapp-automation" className="ll-m-link">
                  💬 <strong className="ll-plat-name">WhatsApp Automation</strong> <span className="ll-plat-sub">(AI CRM)</span>
                </a>
                <span className="ll-m-sep">•</span>

                {/* Repeated for smooth infinite scroll */}
                <a href="https://lysarqsolutions.com/platforms/lysarq-learn" className="ll-m-link">
                  🎓 <strong className="ll-plat-name">LysarQLearN</strong> <span className="ll-plat-sub">(LMS &amp; Auto-Grading)</span>
                </a>
                <span className="ll-m-sep">•</span>
                <a 
                  href="https://msme-intelligence.lysarqsolutions.com" 
                  className="ll-m-link" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (window.location.hostname.includes('msme-intelligence') || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                      e.preventDefault();
                      handleNavigate('home');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  📊 <strong className="ll-plat-name">MSME BI</strong> <span className="ll-plat-sub">(Business Intelligence)</span>
                </a>
                <span className="ll-m-sep">•</span>
                <a href="https://lysarqsolutions.com/platforms/digital-factory" className="ll-m-link">
                  🏭 <strong className="ll-plat-name">Digital Factory</strong> <span className="ll-plat-sub">(Workflow Automation)</span>
                </a>
                <span className="ll-m-sep">•</span>
                <a href="https://lysarqsolutions.com/platforms/circuit-builder" className="ll-m-link">
                  ⚡ <strong className="ll-plat-name">Circuit Builder</strong> <span className="ll-plat-sub">(Virtual Labs)</span>
                </a>
                <span className="ll-m-sep">•</span>
                <a href="https://lysarqsolutions.com/platforms/whatsapp-automation" className="ll-m-link">
                  💬 <strong className="ll-plat-name">WhatsApp Automation</strong> <span className="ll-plat-sub">(AI CRM)</span>
                </a>
                <span className="ll-m-sep">•</span>
              </div>
            </div>
            <div className="ll-action-group">
              <a href="https://lysarqsolutions.com/platforms" className="ll-cta-link">Explore Now →</a>
              <button className="ll-close-btn" onClick={closeAnnouncementBanner} aria-label="Dismiss">✕</button>
            </div>
          </div>
        </div>
      )}

      {/* ✦ BRIDGED DUAL-TIER EXECUTIVE NAVIGATION DOCK ✦ */}
      <div className="lq-dual-nav-hub" ref={navHubRef}>
        {/* Tier 1: Global Platform Navigation */}
        <nav className={`navbar navbar-expand-lg navbar-light modern-navbar lq-navbar lq-hub-tier-top ${isToolsDropdownOpen ? 'has-dropdown-open' : ''}`} aria-label="Global Platform Navigation">
          <a className="navbar-brand lq-navbar-brand d-flex align-items-center" href="https://lysarqsolutions.com/" title="Lysarq Solutions Home">
            <img 
              src={lysarqLogoImg} 
              alt="Lysarq Solutions" 
              className="lq-main-logo" 
              height="32"
              style={{ height: '32px', width: 'auto', maxHeight: '32px', objectFit: 'contain' }}
              loading="eager"
            />
          </a>

          {/* Mobile Right Controls: Bell + Hamburger */}
          <div className="lq-mobile-header-actions">
            {renderNotificationCenter()}

            <button 
              className="navbar-toggler lq-mobile-toggler" 
              type="button" 
              onClick={() => setIsMobileMenuOpen(prev => !prev)}
              aria-label="Toggle navigation"
              id="mobile-menu-btn"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>

          <div className="navbar-nav lq-navbar-nav ms-auto align-items-center">
            <a className="nav-item nav-link lq-nav-link" href="https://lysarqsolutions.com/">Home</a>
            <a 
              className="nav-item nav-link lq-nav-link" 
              href="https://msme-intelligence.lysarqsolutions.com" 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={(e) => {
                if (window.location.hostname.includes('msme-intelligence') || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                  e.preventDefault();
                  handleNavigate('home');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              MSME BI
            </a>

            <div 
              className={`nav-item dropdown lq-dropdown ${isToolsDropdownOpen ? 'show' : ''}`} 
              ref={toolsDropdownRef}
            >
              <a 
                href="https://lysarqsolutions.com/platforms"
                role="button"
                className={`nav-link dropdown-toggle lq-nav-link lq-dropdown-toggle ${isToolsDropdownOpen ? 'show' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  setIsToolsDropdownOpen(prev => !prev);
                }}
                aria-expanded={isToolsDropdownOpen}
              >
                Tools
              </a>

              <div className={`dropdown-menu lq-dropdown-menu fade-down ${isToolsDropdownOpen ? 'show open' : ''}`}>
                <a className="dropdown-item lq-dropdown-item" href="https://lysarqsolutions.com/platforms">
                  All tools
                </a>
                <a 
                  className="dropdown-item lq-dropdown-item" 
                  href="https://msme-intelligence.lysarqsolutions.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (window.location.hostname.includes('msme-intelligence') || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                      e.preventDefault();
                      handleNavigate('home');
                      setIsToolsDropdownOpen(false);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  📊 MSME BI
                </a>
                <a className="dropdown-item lq-dropdown-item" href="https://lysarqsolutions.com/platforms/lysarq-learn">
                  🎓 LMS &amp; Grading
                </a>
                <a className="dropdown-item lq-dropdown-item" href="https://lysarqsolutions.com/platforms/digital-factory">
                  🏭 Digital Factory
                </a>
                <a className="dropdown-item lq-dropdown-item" href="https://lysarqsolutions.com/platforms/circuit-builder">
                  ⚡ Circuit Builder
                </a>
                <a className="dropdown-item lq-dropdown-item" href="https://lysarqsolutions.com/platforms/whatsapp-automation">
                  💬 WhatsApp Automation
                </a>
                <div className="dropdown-divider" style={{ borderColor: '#e2e8f0', margin: '0.35rem 0' }} />
                <a 
                  className="dropdown-item lq-dropdown-item" 
                  href="/?page=methodology" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigate('methodology');
                    setIsToolsDropdownOpen(false);
                  }}
                >
                  📖 Methodology &amp; Pipeline
                </a>
                <a 
                  className="dropdown-item lq-dropdown-item" 
                  href="/?page=about" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigate('about');
                    setIsToolsDropdownOpen(false);
                  }}
                >
                  ℹ️ About Lysarq MSME
                </a>
                <a 
                  className="dropdown-item lq-dropdown-item" 
                  href="/?page=faq" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigate('faq');
                    setIsToolsDropdownOpen(false);
                  }}
                >
                  ❓ Frequently Asked Questions
                </a>
              </div>
            </div>

            <a className="nav-item nav-link lq-nav-link" href="https://lysarqsolutions.com/contact">Contact Us</a>
            <a className="nav-item nav-link btn btn-primary py-2 px-4 ms-lg-3 lq-btn-consulting" href="https://lysarqsolutions.com/services" title="AI Readiness Assessment">Does your business need AI?</a>
          </div>
        </nav>

        {/* Cohesive Hairline Bridge Divider */}
        <div className="lq-hub-bridge-divider" aria-hidden="true" />

        {/* Tier 2: MSME Workspace Controls Bar */}
        <header className="app-header secondary-msme-header lq-hub-tier-bottom">
          <div className="header-content">
            <div 
              className="workspace-brand-link" 
              onClick={() => handleNavigate('home')} 
              title="MSME Business Insights Workspace"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleNavigate('home'); }}
            >
              <span className="workspace-brand-title">MSME Business Insights</span>
              <span className="workspace-brand-badge">MARKET ASSISTANT</span>
            </div>

            {/* Global Search / Command Bar Trigger */}
            <button 
              type="button" 
              className="header-search-btn"
              onClick={() => setIsCommandPaletteOpen(true)}
              aria-label="Open Search & Command Center"
              title="Open Quick Search (⌘K / Ctrl+K)"
            >
              <span className="search-placeholder">
                <IconSearch width={14} height={14} style={{ color: 'var(--brand-accent, #2563eb)' }} />
                <span>{t.searchPlaceholder || 'Search shops, pin codes, trades across India...'}</span>
              </span>
              <kbd className="search-kbd">⌘K</kbd>
            </button>

            <LocationSelector
              selectedDistrict={district}
              onSelectDistrict={setDistrict}
              regions={regions}
              currentDistrictName={currentDistrictName}
            />

            <div className="desktop-header-controls">
              {renderNotificationCenter()}
              <VisitorCounter lang={language} />
              <select
                value={language}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="language-selector"
                aria-label="Select language"
              >
                <option value="en">English</option>
                <option value="ta">தமிழ்</option>
                <option value="ml">മലയാളം</option>
                <option value="hi">हिन्दी</option>
              </select>
            </div>
          </div>
        </header>
      </div>

      {/* ✦ MOBILE SLIDE-OVER DRAWER MODAL ✦ */}
      <div 
        className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`} 
        onClick={closeMobileMenu}
      />
      <div className={`mobile-menu-modal ${isMobileMenuOpen ? 'active' : ''}`}>
        <div className="mobile-menu-header">
          <a className="mobile-menu-logo" href="https://lysarqsolutions.com/" onClick={closeMobileMenu}>
            <img 
              src={lysarqLogoImg} 
              alt="Lysarq Solutions" 
              width="116"
              height="32"
              style={{ height: '32px', width: '116px', objectFit: 'contain' }}
            />
          </a>
          <button className="mobile-menu-close" onClick={closeMobileMenu} aria-label="Close menu">
            ✕
          </button>
        </div>

        {/* Mobile Language Switcher */}
        <div className="mobile-menu-lang-section">
          <span className="mobile-menu-lang-label">{t.languageLabel || 'Language / மொழி / ഭാഷ / भाषा:'}</span>
          <div className="mobile-menu-lang-chips">
            <button 
              type="button"
              className={`mobile-lang-chip ${language === 'en' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('en')}
            >
              English
            </button>
            <button 
              type="button"
              className={`mobile-lang-chip ${language === 'ta' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('ta')}
            >
              தமிழ்
            </button>
            <button 
              type="button"
              className={`mobile-lang-chip ${language === 'ml' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('ml')}
            >
              മലയാളം
            </button>
            <button 
              type="button"
              className={`mobile-lang-chip ${language === 'hi' ? 'active' : ''}`}
              onClick={() => handleLanguageChange('hi')}
            >
              हिन्दी
            </button>
          </div>
        </div>

        {/* Mobile Total Visitors Section */}
        <div className="mobile-menu-telemetry-section">
          <div className="mobile-menu-telemetry-header">
            <span className="mobile-menu-telemetry-label">
              <span className="live-dot" />
              <span>{t.platformVisitors || 'Platform Visitors'}</span>
            </span>
          </div>
          <VisitorCounter lang={language} />
        </div>
        
        <nav className="mobile-menu-nav">
          <a href="https://lysarqsolutions.com/" className="mobile-menu-item" onClick={closeMobileMenu}>Home</a>
          <a 
            href="https://msme-intelligence.lysarqsolutions.com" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="mobile-menu-item active" 
            style={{ color: '#2563eb', fontWeight: 700 }} 
            onClick={(e) => {
              closeMobileMenu();
              if (window.location.hostname.includes('msme-intelligence') || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                e.preventDefault();
                handleNavigate('home');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            MSME BI
          </a>
          
          <div className="mobile-menu-section">
            <div 
              className={`mobile-menu-section-header ${isMobileToolsOpen ? 'active' : ''}`} 
              onClick={() => setIsMobileToolsOpen(prev => !prev)}
            >
              <span>{t.tools || 'Tools'}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: isMobileToolsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            <div className={`mobile-menu-section-content ${isMobileToolsOpen ? 'active' : ''}`}>
              <a href="https://lysarqsolutions.com/platforms" className="mobile-menu-subitem" onClick={closeMobileMenu}>
                <span className="lq-item-icon">🗂️</span>
                <span className="lq-item-label">{t.allTools || 'All tools'}</span>
              </a>
              <div className="lq-dropdown-divider" />
              <a 
                href="https://msme-intelligence.lysarqsolutions.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="mobile-menu-subitem" 
                onClick={(e) => {
                  closeMobileMenu();
                  if (window.location.hostname.includes('msme-intelligence') || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                    e.preventDefault();
                    handleNavigate('home');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                <span className="lq-item-icon">📊</span>
                <span className="lq-item-label">MSME BI</span>
              </a>
              <a href="https://lysarqsolutions.com/platforms/lysarq-learn" className="mobile-menu-subitem" onClick={closeMobileMenu}>
                <span className="lq-item-icon">🎓</span>
                <span className="lq-item-label">LMS &amp; Grading</span>
              </a>
              <a href="https://lysarqsolutions.com/platforms/digital-factory" className="mobile-menu-subitem" onClick={closeMobileMenu}>
                <span className="lq-item-icon">🏭</span>
                <span className="lq-item-label">Digital Factory</span>
              </a>
              <a href="https://lysarqsolutions.com/platforms/circuit-builder" className="mobile-menu-subitem" onClick={closeMobileMenu}>
                <span className="lq-item-icon">⚡</span>
                <span className="lq-item-label">Circuit Builder</span>
              </a>
              <a href="https://lysarqsolutions.com/platforms/whatsapp-automation" className="mobile-menu-subitem" onClick={closeMobileMenu}>
                <span className="lq-item-icon">💬</span>
                <span className="lq-item-label">WhatsApp Automation</span>
              </a>
            </div>
          </div>
          
          <a href="https://lysarqsolutions.com/contact" className="mobile-menu-item" onClick={closeMobileMenu}>{t.contactUs || 'Contact Us'}</a>
          <a href="https://lysarqsolutions.com/services" className="mobile-menu-item mobile-menu-cta" onClick={closeMobileMenu}>{t.needAi || 'Does your business need AI?'}</a>
        </nav>
      </div>

    {/* Intelligence Context Ribbon */}
      <div className="global-context-bar">
        <div className="context-bar-content">
          <div className="context-location-trail">
            <IconGlobe width={14} height={14} style={{ color: 'var(--color-brand-primary)' }} />
            <span>{currentDistrictName}</span>
          </div>
          <div className="context-meta-group">
            <div className="context-status-cluster">
              <div className="context-meta-item">
                <span className="context-freshness-dot" style={{ backgroundColor: isUpdatingContext ? '#3b82f6' : '#22c55e' }} />
                <span>
                  {isUpdatingContext ? `${t.updatingMarket || 'Updating market data for'} ${currentDistrictName}...` : freshnessText}
                </span>
              </div>
              <TrustLayerPopover district={district} freshnessText={freshnessText} />
            </div>
            <div className="context-quick-actions">
              <button
                type="button"
                className="context-action-btn"
                onClick={() => openModal('preset')}
                title="Save this view to your bookmarks (Ctrl+S / ⌘S)"
              >
                <span>{t.saveView || 'Save View'}</span>
                <kbd className="context-action-kbd">⌘S</kbd>
              </button>
              <button
                type="button"
                className="context-action-btn"
                onClick={() => openModal('export')}
                title="Download data report for this area"
              >
                <span>{t.download || 'Download'}</span>
              </button>
              <button
                type="button"
                className="context-action-btn"
                onClick={() => openModal('share')}
                title="Share link to this page"
              >
                <span>{t.share || 'Share'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Analytical Workspaces with Max-Width Container */}
      <main className="app-main">
        <div className="app-content-container">
          <Suspense fallback={<WorkspaceFallback />}>
            {visitedPages.has('home') && (
              <div hidden={currentPage !== 'home'}>
                <DashboardPage 
                  userPincode={userPincode} 
                  language={language} 
                  district={district}
                  onNavigate={handleNavigate}
                />
              </div>
            )}
            {visitedPages.has('analytics') && (
              <div hidden={currentPage !== 'analytics'}>
                <AnalyticsPage 
                  userPincode={userPincode} 
                  language={language} 
                  district={district}
                  onNavigate={handleNavigate}
                />
              </div>
            )}
            {visitedPages.has('search') && (
              <div hidden={currentPage !== 'search'}>
                <SearchPage 
                  userPincode={userPincode} 
                  setUserPincode={setUserPincode} 
                  language={language} 
                  district={district}
                  selectedSector={selectedSector}
                  setSelectedSector={setSelectedSector}
                  onNavigate={handleNavigate}
                />
              </div>
            )}
            {visitedPages.has('trends') && (
              <div hidden={currentPage !== 'trends'}>
                <TrendsPage 
                  language={language} 
                  district={district}
                  onNavigate={handleNavigate}
                />
              </div>
            )}
            {visitedPages.has('forecast') && (
              <div hidden={currentPage !== 'forecast'}>
                <ForecastPage 
                  userPincode={userPincode} 
                  language={language} 
                  district={district}
                  onNavigate={handleNavigate}
                />
              </div>
            )}
            {visitedPages.has('profile') && (
              <div hidden={currentPage !== 'profile'}>
                <ProfilePage language={language} onNavigate={handleNavigate} />
              </div>
            )}
            {/* Authoritative Citable Public Pages */}
            {currentPage === 'about' && (
              <AboutPage onNavigate={handleNavigate} />
            )}
            {currentPage === 'methodology' && (
              <MethodologyPage onNavigate={handleNavigate} />
            )}
            {currentPage === 'data-sources' && (
              <DataSourcesPage onNavigate={handleNavigate} />
            )}
            {currentPage === 'data-dictionary' && (
              <DataDictionaryPage onNavigate={handleNavigate} />
            )}
            {currentPage === 'faq' && (
              <FAQPage onNavigate={handleNavigate} />
            )}
          </Suspense>
        </div>

        {/* Global Authoritative Site Footer (AEO / GEO Entity Footprint) */}
        <SiteFooter onNavigate={handleNavigate} currentDistrict={district} />
      </main>

      {/* Floating Liquid Glass Bottom Navigation Dock with Sliding Pill Highlight */}
      <nav 
        className="liquid-glass-dock" 
        aria-label="Primary Navigation"
      >
        <div className="dock-glass-surface" ref={dockContainerRef} role="tablist">
          {/* Liquid Sliding Active Pill Indicator */}
          <div 
            ref={indicatorRef}
            className="dock-sliding-indicator"
          />

          {NAV_ITEMS.map((item) => {
            const isActive = currentPage === item.id;
            const ItemIcon = item.Icon;
            return (
              <button 
                key={item.id}
                ref={(el) => (tabButtonRefs.current[item.id] = el)}
                type="button"
                className={`dock-pill ${isActive ? 'active' : ''}`}
                onClick={() => handleNavigate(item.id)}
                onPointerDown={() => applyIndicatorPosition(item.id)}
                role="tab"
                aria-selected={isActive}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="dock-pill-icon"><ItemIcon width={16} height={16} /></span>
                <span className="dock-pill-label">{t[item.key]}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Floating Lysarq Solutions CTA Pill (Commented out) */}
      {/*
      <a
        ref={ctaRef}
        href="https://lysarqsolutions.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="lysarq-cta-pill"
        aria-label='Lysarq Solutions — "Building AI Solutions"'
      >
        <div className="cta-pill-icon-well">
          <IconBrandLogo width={20} height={20} className="cta-pill-logo" />
        </div>
        <div className="cta-pill-body">
          <div className="cta-pill-headline">
            <span className="cta-pill-prefix">Powered by</span>
            <span className="cta-pill-brand">Lysarq Solutions</span>
          </div>
          <div className="cta-pill-tagline">
            <span className="cta-pill-quote-char">&ldquo;</span>Building AI Solutions<span className="cta-pill-quote-char">&rdquo;</span>
          </div>
        </div>
        <div className="cta-pill-arrow-wrap">
          <IconArrowRight width={11} height={11} className="cta-pill-arrow" />
        </div>
      </a>
      */}
    </div>
  );
}

export default function App() {
  return (
    <IntelligenceProvider>
      <HoneypotDecoy />
      <AppContent />
    </IntelligenceProvider>
  );
}
